import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { sendEvent } from './utils/analytics';
import Spinner from './Spinner';

const LOCAL_STORAGE_KEY = 'seated_events_cache';
const LOCAL_CACHE_DURATION = 3600 * 24; // 24 hours in seconds

const SeatedEventsModal = () => {
  const [loading, setLoading] = useState(true);
  const [cachedContent, setCachedContent] = useState(null);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  
  const baseTitle = 'Shows | Adam Ten';
  const description = 'View upcoming shows and purchase tickets for Adam Ten.';
  const currentUrl = 'https://www.adam-ten.com/shows';

  // Track modal open in Google Analytics (only if consent is given)
  useEffect(() => {
    sendEvent({
      category: 'Modal',
      action: 'Opened Seated Events Modal',
      label: 'Seated Events Modal',
    });
  }, []);

  // Always load Seated CSS, even when using cached content
  useEffect(() => {
    // Check if CSS is already loaded
    const existingLink = document.getElementById('seated-widget-css');
    
    if (!existingLink) {
      const link = document.createElement('link');
      link.id = 'seated-widget-css';
      link.rel = 'stylesheet';
      link.href = 'https://widget.seated.com/app-v3.css';
      document.head.appendChild(link);
    }

    return () => {
      // Don't remove CSS on unmount as it might be needed for other instances
    };
  }, []);

  // Check cache first, then load widget if needed
  useEffect(() => {
    // Check local storage for cached content
    const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (cached) {
      try {
        const { html, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < LOCAL_CACHE_DURATION * 1000) {
          // Cache is valid, use it
          console.log('Loading Seated events from cache');
          setCachedContent(html);
          setLoading(false);
          setEventsLoaded(true);
          return;
        } else {
          // Cache expired, remove it
          console.log('Seated cache expired, fetching fresh data');
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
      } catch (e) {
        // Invalid cache data, remove it
        console.error('Invalid Seated cache, removing:', e);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }

    // No valid cache, load the widget
    console.log('No valid cache, loading Seated widget');
    
    let waitForElement;
    let observer;
    
    const setupObserver = () => {
      // Wait for the DOM element to be available
      waitForElement = setInterval(() => {
        const widgetContainer = document.getElementById('seated-55fdf2c0');
        
        if (widgetContainer) {
          clearInterval(waitForElement);
          console.log('Widget container found, setting up observer');
          
          // Track if events have been detected to avoid multiple state updates
          let eventsDetected = false;
          
          // Shared function to cache widget HTML
          const cacheWidgetHTML = () => {
            try {
              // Check if localStorage is available
              if (typeof Storage === 'undefined') {
                console.warn('localStorage is not available');
                return;
              }
              
              setTimeout(() => {
                const widgetHTML = widgetContainer.innerHTML;
                console.log('Widget HTML length:', widgetHTML ? widgetHTML.length : 0);
                
                if (widgetHTML && widgetHTML.trim() !== '') {
                  const cacheData = {
                    html: widgetHTML,
                    timestamp: Date.now()
                  };
                  
                  console.log('Caching Seated widget HTML, size:', JSON.stringify(cacheData).length, 'bytes');
                  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cacheData));
                  
                  // Verify it was saved
                  const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
                  if (saved) {
                    console.log('Cache saved successfully, verified');
                  } else {
                    console.error('Cache save failed - localStorage.getItem returned null');
                  }
                } else {
                  console.warn('Widget HTML is empty, not caching. HTML:', widgetHTML);
                }
              }, 1000);
            } catch (error) {
              console.error('Error caching widget HTML:', error);
              // Check if it's a quota exceeded error
              if (error.name === 'QuotaExceededError') {
                console.error('localStorage quota exceeded, cannot cache');
              }
            }
          };
          
          // Periodic check for actual event rows (not just "no events" message)
          let checkInterval = setInterval(() => {
            if (eventsDetected) return;
            
            const hasEventRows = widgetContainer.querySelector('.seated-event-row');
            const hasNoEventsMessage = widgetContainer.querySelector('.seated-no-events');
            
            // Only show content when we have actual event rows
            if (hasEventRows) {
              console.log('Widget events loaded - event rows detected');
              eventsDetected = true;
              clearInterval(checkInterval);
              setLoading(false);
              setEventsLoaded(true);
              cacheWidgetHTML();
            } else if (hasNoEventsMessage && !hasEventRows) {
              // If we see "no events" message but no event rows after a delay,
              // events might still be loading, so wait a bit more
              // This handles the case where "no events" appears before events load
            }
          }, 200); // Check every 200ms
          
          // Observer to detect when widget content loads (for faster detection)
          observer = new MutationObserver((mutations) => {
            if (eventsDetected) return;
            
            mutations.forEach((mutation) => {
              if (mutation.addedNodes.length > 0) {
                // Check if actual events have loaded
                const hasEventRows = widgetContainer.querySelector('.seated-event-row');
                if (hasEventRows) {
                  console.log('Widget events loaded via MutationObserver');
                  eventsDetected = true;
                  clearInterval(checkInterval);
                  setLoading(false);
                  setEventsLoaded(true);
                  cacheWidgetHTML();
                }
              }
            });
          });

          observer.observe(widgetContainer, {
            childList: true,
            subtree: true,
          });
          
          // Store observer and interval for cleanup
          widgetContainer._seatedObserver = observer;
          widgetContainer._seatedCheckInterval = checkInterval;
          
          // Fallback: if no events detected after 5 seconds, show anyway and try to cache
          setTimeout(() => {
            if (!eventsDetected) {
              eventsDetected = true;
              clearInterval(checkInterval);
              console.log('Timeout reached - showing widget');
              setLoading(false);
              setEventsLoaded(true);
              // Try to cache even if we didn't detect event rows (might be a different structure)
              cacheWidgetHTML();
            }
          }, 5000);
        }
      }, 100);
    };

    // Check if script is already loaded
    const existingScript = document.getElementById('seated-widget-script');
    
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'seated-widget-script';
      script.src = 'https://widget.seated.com/app.js';
      script.async = true;
      
      // Setup observer after script loads
      script.onload = () => {
        console.log('Seated script loaded');
        setupObserver();
        
        // This timeout is now handled in setupObserver
      };
      
      script.onerror = () => {
        console.error('Failed to load Seated script');
        setLoading(false);
      };
      
      document.body.appendChild(script);
    } else {
      // Script already exists, setup observer immediately
      console.log('Seated script already loaded');
      setupObserver();
      // setupObserver will handle detecting events and setting eventsLoaded
    }

    return () => {
      if (waitForElement) clearInterval(waitForElement);
      const widgetContainer = document.getElementById('seated-55fdf2c0');
      if (widgetContainer?._seatedObserver) {
        widgetContainer._seatedObserver.disconnect();
      }
      if (widgetContainer?._seatedCheckInterval) {
        clearInterval(widgetContainer._seatedCheckInterval);
      }
      // Don't cleanup script on unmount - keep it for subsequent opens
    };
  }, []);

  return (
    <div className="seated-modal max-width">
      <Helmet>
        <title>{baseTitle}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={baseTitle} />
        <meta property="og:description" content={description} />
        <meta name="twitter:title" content={baseTitle} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
      </Helmet>

      {loading && <Spinner />}

      <div style={{ display: loading ? 'none' : 'block' }}>
        <h2>Upcoming shows</h2>

        <div 
          className={`seated-widget-container ${eventsLoaded ? 'events-loaded' : 'events-loading'}`}
          style={{ opacity: eventsLoaded ? 1 : 0, transition: 'opacity 0.3s ease-in' }}
        >
          {cachedContent ? (
            <div 
              id="seated-55fdf2c0" 
              data-artist-id="7f8d5510-2267-48af-8766-1bfa21eaf413" 
              data-css-version="3"
              dangerouslySetInnerHTML={{ __html: cachedContent }}
            />
          ) : (
            <div 
              id="seated-55fdf2c0" 
              data-artist-id="7f8d5510-2267-48af-8766-1bfa21eaf413" 
              data-css-version="3"
            ></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeatedEventsModal;

